import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowRightIcon, DocumentArrowDownIcon, DocumentArrowUpIcon, ComputerDesktopIcon, GlobeAltIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { usePageTitle as usePageTitleContext } from '../contexts/PageTitleContext';
import { useAuth } from '../contexts/AuthContext';

const allLanguages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

const Translations: React.FC = () => {
  const { setPageTitle } = usePageTitleContext();
  const { user } = useAuth();
  const isSysAdmin = user?.role === 'sysadmin';
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [channel, setChannel] = useState<'web' | 'desktop'>('web');
  const [showExportAllMenu, setShowExportAllMenu] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const exportAllMenuRef = useRef<HTMLDivElement>(null);

  // Filter languages based on user role and translator permissions
  const languages = useMemo(() => {
    // Sysadmins can see all languages
    if (user?.role === 'sysadmin') {
      return allLanguages;
    }
    
    // Translators can see English (baseline) + their assigned language
    // Role format: translator_es, translator_de, etc.
    if (user?.role && user.role.startsWith('translator_')) {
      const translatorLang = user.role.replace('translator_', ''); // Extract language code
      const allowedLanguages = ['en', translatorLang]; // English is always accessible
      return allLanguages.filter(lang => allowedLanguages.includes(lang.code));
    }
    
    // Other roles don't have access (shouldn't reach here due to backend middleware)
    return [];
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportAllMenuRef.current && !exportAllMenuRef.current.contains(event.target as Node)) {
        setShowExportAllMenu(false);
      }
    };

    if (showExportAllMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportAllMenu]);

  const handleExportAll = async (format?: 'zip' | 'excel') => {
    setIsExporting(true);
    try {
      const formatParam = format || 'zip';
      const blob = await apiService.exportAllLanguages(channel, formatParam);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = formatParam === 'excel' ? '.xlsx' : '.zip';
      a.download = `all-translations-${channel}${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`All ${channel} translations exported successfully`);
      setShowExportAllMenu(false);
    } catch (error: any) {
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportAll = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a ZIP file');
      return;
    }

    setIsImporting(true);
    try {
      // Import all languages - don't pass langId, so it expects {lang}/{namespace}.json format
      const result = await apiService.importTranslations(file, undefined, channel);
      
      const message = `Import completed: ${result.inserted} inserted, ${result.updated} updated, ${result.skipped} skipped`;
      const hasErrors = result.errors && result.errors.length > 0;
      const hasWarnings = result.warnings && result.warnings.length > 0;
      if (hasErrors) {
        toast.error(`${message}. ${result.errors.length} errors occurred.`, {
          duration: 5000,
        });
        console.error('Import errors:', result.errors);
      } else {
        toast.success(hasWarnings ? `${message}. ${result.warnings.length} warnings (missing keys vs English).` : message);
        if (hasWarnings) {
          console.warn('Import warnings (missing keys):', result.warnings);
        }
      }

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(`Import failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Update header with buttons
  useEffect(() => {
    const rightSideButtons = (
      <div className="flex items-center space-x-2">
        {/* Channel Toggle - Only show for sysadmins */}
        {isSysAdmin && (
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setChannel('web')}
              className={`p-2 rounded-md transition-colors ${
                channel === 'web'
                  ? 'bg-white dark:bg-gray-700 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Web Channel"
            >
              <GlobeAltIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setChannel('desktop')}
              className={`p-2 rounded-md transition-colors ${
                channel === 'desktop'
                  ? 'bg-white dark:bg-gray-700 text-governate-600 dark:text-governate-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Desktop Channel"
            >
              <ComputerDesktopIcon className="w-5 h-5" />
            </button>
          </div>
        )}
        {/* Import/Export buttons - Only show for sysadmins (translators use individual language pages) */}
        {isSysAdmin && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleImportAll}
              className="hidden"
              id="import-all-input"
            />
            <label
              htmlFor="import-all-input"
              className={`p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                isImporting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              title="Import All Languages"
            >
              <DocumentArrowUpIcon className="w-5 h-5" />
            </label>
            <div className="relative inline-block" ref={exportAllMenuRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowExportAllMenu(prev => !prev);
                }}
                disabled={isExporting}
                className="p-2 bg-governate-500 text-white rounded-lg hover:bg-governate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                title="Export All Languages"
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                <ChevronDownIcon className="w-3 h-3" />
              </button>
              {showExportAllMenu && (
                <div className="absolute right-0 top-full mt-2 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[100]">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleExportAll('zip');
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                  >
                    Export All ZIP
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleExportAll('excel');
                    }}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                  >
                    Export All Excel
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );

    setPageTitle('Translation Management', undefined, {
      rightSideButtons,
    });

    return () => {
      setPageTitle('', undefined, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, isImporting, isExporting, isSysAdmin, showExportAllMenu]);

  return (
    <div className="space-y-6">
      {/* Language Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {languages.map((lang) => (
          <LanguageCard key={lang.code} language={lang} channel={channel} />
        ))}
      </div>
    </div>
  );
};

interface LanguageCardProps {
  language: { code: string; name: string; flag: string };
  channel: 'web' | 'desktop';
}

const LanguageCard: React.FC<LanguageCardProps> = ({ language, channel }) => {
  const navigate = useNavigate();
  const namespaces = ['common', 'login', 'forgotPassword', 'dashboard', 'sidebar'];

  return (
    <div
      className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(`/translations/${language.code}?channel=${channel}`)}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-3xl">{language.flag}</span>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{language.name}</h3>
            <p className="text-sm text-gray-500">{language.code.toUpperCase()}</p>
          </div>
        </div>
        <ArrowRightIcon className="w-5 h-5 text-gray-400" />
      </div>

      <div className="text-sm text-gray-600">
        <p>
          <span className="font-medium">{namespaces.length}</span> namespace{namespaces.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default Translations;
